import React, { type PropsWithChildren, useContext } from "react";
import { createClasses } from "@bloomberg/media-ui-core";

import ErrorBoundary from "~components/shared/errorBoundary";
import { GridModule } from "~components/shared/grid/gridModule";
import { PageContext } from "~context/PageContext";
import type { ModuleTracking, TrackingAttributes } from "~types/lineup";
import { ZoneColumnCombo } from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";
import { getCanonicalId, getProvenance } from "~utils/moduleProvenance";
import trackStoriesInModules from "~utils/trackStoriesInModules";

import integrityCheck from "./Module.integrity";
import styles from "./styles.module.css";

export interface ModuleProps {
    className?: string;
    id: string;
    duplicateOf: string | undefined;
    abbaConfigKey?: string;
    autofillData?: object;
    dataComponent?: string;
    relatedStoryCount?: number;
    storyId?: string;
    trackingType?: string;
    moduleTracking?: ModuleTracking;
    storyCount?: number;
    useGrid?: boolean;
    gridClassName?: string;
    isPhoenix?: boolean;
    renderingLocation?: ZoneColumnCombo;
    supportedRegions?: ZoneColumnCombo[];
    moduleIndex?: number;
    zoneName?: string;
}

interface AbbaAttributes {
    "data-abba-config-key"?: string;
    "data-bound"?: "deferred";
}

const Module: React.FC<PropsWithChildren<ModuleProps>> = (props) => {
    const {
        className,
        children,
        id,
        abbaConfigKey,
        autofillData,
        dataComponent,
        relatedStoryCount,
        storyId,
        storyCount,
        trackingType,
        moduleTracking,
        gridClassName,
        isPhoenix = false,
        renderingLocation = ZoneColumnCombo.unsupported,
        supportedRegions = [],
        moduleIndex = 1,
        zoneName,
    } = props;

    console.warn(`Legacy Detection - module - ${dataComponent}`);

    const { useGrid } = useContext(PageContext);

    const trackingAttributes: TrackingAttributes | null = trackStoriesInModules(
        {
            storyId,
            storyCount,
            trackingType,
            moduleTracking,
            renderingLocation,
            isPhoenix,
            moduleTreatment: dataComponent,
            zoneName,
        }
    );

    const integrity = integrityCheck(props);

    if (integrity.length) return integrityError(integrity);

    const abbaAttributes: AbbaAttributes = {};

    if (abbaConfigKey) {
        abbaAttributes["data-abba-config-key"] = abbaConfigKey;
        abbaAttributes["data-bound"] = "deferred";
    }

    const moduleProvenance = getProvenance(props);
    const phoenixModuleOnPhoenix = useGrid && isPhoenix;
    const retroModuleOnRetro = !useGrid && !isPhoenix;

    let validModule;

    // If a module declares its supportedRegions
    // and renderingLocation, those should be the
    // determinate of validity in either direction.
    // If a module does not pass these values, we
    // use the isPhoenix prop to determine validity - BW 11/13/23
    if (supportedRegions.length > 0 && renderingLocation) {
        validModule = supportedRegions.includes(renderingLocation);
    } else {
        validModule = phoenixModuleOnPhoenix || retroModuleOnRetro;
    }

    const unsupportedPlaceholder = (
        <div
            id={id}
            className={styles.unsupportedRegion}
            data-component="placeholder-module"
        />
    );

    const component = (
        <ErrorBoundary>
            <section
                className={createClasses(className, {
                    "client-autofill": Boolean(autofillData),
                    [styles.shadow]: moduleProvenance === "shadow" && !useGrid,
                })}
                id={id}
                data-module-index={moduleIndex}
                data-canonical={getCanonicalId(props)}
                data-module-provenance={moduleProvenance}
                data-component={dataComponent}
                data-related-stories-count={relatedStoryCount}
                {...abbaAttributes}
                {...trackingAttributes}
            >
                {children}
            </section>
        </ErrorBoundary>
    );

    if (useGrid) {
        return (
            <ErrorBoundary>
                <GridModule
                    className={createClasses(gridClassName, {
                        [styles.shadowGrid]:
                            moduleProvenance === "shadow" && useGrid,
                    })}
                >
                    {validModule ? component : unsupportedPlaceholder}
                </GridModule>
            </ErrorBoundary>
        );
    }

    return validModule ? component : unsupportedPlaceholder;
};

export default Module;
